import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardAction, CardFooter } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { CheckCircle2, AlertTriangle, XCircle, TrendingUp, Loader2 } from 'lucide-react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { advancedReportsAPI } from '../../services/api';

interface SLAMetrics {
  overall_compliance: number;
  first_response_compliance: number;
  resolution_compliance: number;
  total_breaches: number;
  at_risk: number;
}

interface SLABreach {
  conversation_id: number;
  contact_name: string;
  priority: string;
  sla_type: 'first_response' | 'resolution';
  target_time: number;
  actual_time: number;
  breach_amount: number;
  breach_percentage: number;
  root_cause?: string;
}

interface AgentSLAPerformance {
  agent_id: number;
  agent_name: string;
  compliance_rate: number;
  breaches: number;
  average_response_time: number;
  average_resolution_time: number;
  performance_tier: 'Excellent' | 'Good' | 'Needs Improvement';
}

interface SLADataState {
  metrics: SLAMetrics;
  breaches: SLABreach[];
  agent_performance: AgentSLAPerformance[];
  compliance_trend: Array<{ date: string; compliance: number }>;
  breach_heatmap: any;
}

interface SLAComplianceTrackingProps {
  startDate: string;
  endDate: string;
  agentId?: string;
  teamId?: string;
}

const getPriorityVariant = (priority: string): 'destructive' | 'outline' => {
  return priority === 'Urgent' ? 'destructive' : 'outline';
};

const getTierVariant = (tier: string): 'secondary' | 'outline' | 'destructive' => {
  if (tier === 'Excellent' || tier === 'Good') return 'secondary';
  return 'destructive';
};

const SLAComplianceTracking: React.FC<SLAComplianceTrackingProps> = ({
  startDate,
  endDate,
  agentId,
  teamId
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SLADataState | null>(null);

  useEffect(() => {
    fetchSLAData();
  }, [startDate, endDate, agentId, teamId]);

  const fetchSLAData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await advancedReportsAPI.getSLACompliance({
        start_date: startDate,
        end_date: endDate,
        agent_id: agentId,
        team_id: teamId
      });

      setData(response);
    } catch (err: any) {
      console.error('Failed to fetch SLA data:', err);
      setError(err.message || 'Failed to load SLA compliance data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert>
        <AlertDescription>No SLA data available for the selected period.</AlertDescription>
      </Alert>
    );
  }

  const trendOptions: ApexOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      sparkline: { enabled: false }
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 90, 100]
      }
    },
    xaxis: {
      categories: data.compliance_trend.map(d => d.date),
      labels: { rotate: -45 }
    },
    yaxis: {
      min: 80,
      max: 100,
      labels: { formatter: (val) => `${val.toFixed(0)}%` }
    },
    colors: ['#1f93ff'],
    tooltip: {
      y: { formatter: (val) => `${val.toFixed(1)}%` }
    }
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Alert>
        <TrendingUp className="h-4 w-4" />
        <AlertDescription>
          <p className="font-medium">Advanced SLA Features Coming Soon!</p>
          <p className="text-xs mt-1">
            Currently showing simplified SLA metrics based on response and resolution times.
            Next release will include: Custom SLA rules, Root cause analysis, Predictive breach alerts, and Advanced automation.
          </p>
        </AlertDescription>
      </Alert>

      {/* SLA Overview Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Overall Compliance</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.metrics.overall_compliance}%
            </CardTitle>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5">
            <Progress value={data.metrics.overall_compliance} className="h-1.5 w-full" />
            <span className="text-xs text-muted-foreground">Target: 95%</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>First Response SLA</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.metrics.first_response_compliance.toFixed(1)}%
            </CardTitle>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5">
            <Progress value={data.metrics.first_response_compliance} className="h-1.5 w-full" />
            <span className="text-xs text-muted-foreground">Target: 96%</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Resolution SLA</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.metrics.resolution_compliance.toFixed(1)}%
            </CardTitle>
          </CardHeader>
          <CardFooter className="flex-col items-start gap-1.5">
            <Progress value={data.metrics.resolution_compliance} className="h-1.5 w-full" />
            <span className="text-xs text-muted-foreground">Target: 90%</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>SLA Breaches</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <XCircle className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.metrics.total_breaches}
            </CardTitle>
          </CardHeader>
          <CardFooter className="gap-2">
            <Badge variant="outline" className="text-xs font-medium border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
              <AlertTriangle className="h-3 w-3" />
              {data.metrics.at_risk} at risk
            </Badge>
          </CardFooter>
        </Card>
      </div>

      {/* Compliance Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">SLA Compliance Trend (Last 7 Days)</CardTitle>
          <CardDescription>Daily compliance rate across all SLA types</CardDescription>
        </CardHeader>
        <CardContent>
          <Chart
            options={trendOptions}
            series={[{ name: 'Compliance Rate', data: data.compliance_trend.map(d => d.compliance) }]}
            type="area"
            height={300}
          />
        </CardContent>
      </Card>

      {/* Breaches + Agent Performance */}
      <div className="grid gap-6 lg:grid-cols-7">
        {/* Recent SLA Breaches */}
        <Card className="lg:col-span-4">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Recent SLA Breaches</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Conversation</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Target</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead className="text-right">Breach</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.breaches.map((breach) => (
                  <TableRow key={breach.conversation_id}>
                    <TableCell className="text-sm">#{breach.conversation_id}</TableCell>
                    <TableCell className="text-sm">{breach.contact_name}</TableCell>
                    <TableCell>
                      <Badge variant={getPriorityVariant(breach.priority)}>
                        {breach.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {breach.sla_type === 'first_response' ? 'First Response' : 'Resolution'}
                    </TableCell>
                    <TableCell className="text-right text-sm">{breach.target_time}m</TableCell>
                    <TableCell className="text-right text-sm">{breach.actual_time}m</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="destructive">+{breach.breach_percentage.toFixed(0)}%</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Agent SLA Performance */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Agent SLA Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-5">
              {data.agent_performance.map((agent) => (
                <div key={agent.agent_id} className="space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium">{agent.agent_name}</p>
                      <p className="text-xs text-muted-foreground">
                        Avg Response: {agent.average_response_time}m &nbsp;|&nbsp; Resolution: {Math.floor(agent.average_resolution_time / 60)}h {agent.average_resolution_time % 60}m
                      </p>
                    </div>
                    <Badge variant={getTierVariant(agent.performance_tier)}>
                      {agent.performance_tier}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress value={agent.compliance_rate} className="flex-1 h-2" />
                    <span className="text-sm font-bold w-14 text-right">{agent.compliance_rate.toFixed(1)}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{agent.breaches} breaches</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SLA Performance Insights */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-sm font-medium">SLA Performance Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            <div>
              <p className="text-sm font-medium mb-2">Strengths</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><span className="text-xs text-muted-foreground">First response SLA exceeds target (96.2% vs 96%)</span></li>
                <li><span className="text-xs text-muted-foreground">Overall compliance above 94%</span></li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Areas for Improvement</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><span className="text-xs text-muted-foreground">8 conversations currently at risk of breach</span></li>
                <li><span className="text-xs text-muted-foreground">Resolution SLA slightly below target</span></li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Recommendations</p>
              <ul className="list-disc pl-4 space-y-1">
                <li><span className="text-xs text-muted-foreground">Focus on high-priority conversations first</span></li>
                <li><span className="text-xs text-muted-foreground">Provide additional training for agents below 90%</span></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SLAComplianceTracking;
